import { createFileRoute, notFound } from '@tanstack/react-router'
import ErrorComponent from '../../Components/ErrorComponent'
import StaffPageHeader from '../../Pages/StaffPage/StaffPageHeader'
import StaffDetailsTab from '../../Pages/StaffPage/StaffDetailsTab'
import PageBody from '../../Pages/Layout/PageBody'
import LoadingSpinner from '../../Components/LoadingSpinner'
import PermissionDenied from '../../Pages/PermissionDenied'
import {
    canViewRoute,
    migratedToV2,
} from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'
import { idf } from '../../Queries/queryFormatter'
import StaffPermissionsTab from '../../Pages/StaffPage/StaffPermissionsTab'
import StaffRatesTab from '../../Pages/StaffPage/StaffRatesTab'
import DataStore from '../../State/DataStore'
import { router } from '../../App'
import StaffCollection from '../../State/Collections/StaffCollection'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { getCacheItemById } from '@2/cache'
import { CenterPadPageBody } from '@2/layout/page-body'
import { StaffDetailTab } from '../../version2/pages/staff-page/staff-detail-tab'
import { StaffRateTab } from '../../version2/pages/staff-page/staff-rates-tab'
import { StaffPermissionTab } from '../../version2/pages/staff-page/staff-permission-tab'
import { staffStore } from '../../version2/pages/staff-page/staff-page-store'
import { trpc } from '../../system/trpc'
import { processTRPCData } from '../../Queries/fetchData'
import { cleanupNewEntity } from '../../Utils/deleteEntity'

const tabs = {
    details: {
        label: 'Details',
        id: 'details',
        page: (props) => <StaffDetailsTab {...props} />,
        collections: (id) => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, `staff-details`, params),
    },
    rates: {
        label: 'Rates',
        id: 'rates',
        page: (props) => <StaffRatesTab {...props} />,
        permissions: (params) =>
            canViewRoute(SessionStore.user, `staff-rates`, params),
    },
    permissions: {
        label: 'Permissions',
        id: 'permissions',
        page: (props) => <StaffPermissionsTab {...props} />,
        collections: () => {
            return []
        },
        permissions: (params) =>
            canViewRoute(SessionStore.user, `staff-permissions`, params),
    },
}

export const Route = createFileRoute('/_private/staff/$id')({
    beforeLoad: ({ params, search }) => {
        const activeTab = search?.tab || 'details'
        if (
            !canViewRoute(SessionStore.user, 'staff', params) ||
            !canViewRoute(SessionStore.user, `staff-${activeTab}`, params)
        )
            throw new Error('Permission Denied')
        const staff = getCacheItemById('staff', params.id)
        setLayoutDefaults({
            pageTitle: `${staff?.firstName} ${staff?.lastName}` || 'Staff',
            subMenu: 'resources',
            tabs: [
                {
                    label: 'Details',
                    id: 'details',
                },
                {
                    label: 'Rates',
                    id: 'rates',
                },
                {
                    label: 'Permissions',
                    id: 'permissions',
                },
            ],
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    loaderDeps: ({ search: { tab } }) => ({
        tab,
    }),
    loader: async ({ params, location }) =>
        await loader(params.id, location.search),
    pendingComponent: LoadingSpinner,
    component: Wrapper,
    onLeave: (match) => {
        const { id } = match.params
        const staff = StaffCollection.staffById[id]
        if (
            staff &&
            !staff.createdAt &&
            match.pathname !== router.latestLocation.pathname
        ) {
            cleanupNewEntity(staff, 'staff')
        }
    },
})

const loader = async (id, search) => {
    const { tab } = search
    const activeTab = tab || 'details'
    const data = await trpc.legacy.getStaffPageData.query({ id })
    const processedModels = processTRPCData(data)
    const staff = StaffCollection.staffById[id]
    if (!staff) {
        throw notFound()
    }
    return processedModels
}

function Wrapper() {
    const { tab } = Route.useSearch()
    const params = Route.useParams()
    const activeTab = tab || 'details'
    if (migratedToV2()) {
        return (
            <CenterPadPageBody>
                {tabs[activeTab].page(params)}
            </CenterPadPageBody>
        )
    }

    return (
        <>
            <StaffPageHeader
                tabs={Object.values(tabs).filter((t) => t.permissions(params))}
                selectedTab={activeTab}
                {...params}
            />
            <PageBody>{tabs[activeTab].page(params)}</PageBody>
        </>
    )
}

// Optionally export the tabs configuration if needed elsewhere
export { tabs }
